import { useState } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Mail, Eye, EyeOff } from "lucide-react";
import { fetchApi } from "@/lib/apiBase";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSubmark, setShowSubmark] = useState(true);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetchApi("/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Login failed");
      }

      const data = await res.json();
      localStorage.setItem("adminToken", data.token);
      localStorage.setItem("adminEmail", data.email);
      toast.success("Login successful");
      navigate("/admin/dashboard");
    } catch (error) {
      toast.error(error.message || "Invalid credentials");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F0F0] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex flex-col items-center group">
            <div className="flex items-center justify-center gap-2 mb-2">
            {showSubmark && (
              <img
                src="/images/submark-logo.png"
                alt="DadaDeLuxe submark"
                className="h-8 w-auto"
                onError={() => setShowSubmark(false)}
              />
            )}
            <span className="logo-text text-2xl text-[#111111] block">
              DADADELUXE
            </span>
            </div>
            <span className="text-[11px] uppercase tracking-[0.2em] text-[#888] group-hover:text-[#111111] transition-colors">
              Back to Home
            </span>
          </Link>
        </div>

        <div
          data-testid="admin-login-form"
          className="glass-card p-8"
        >
          <h2 className="script-heading text-xl mb-6 text-center">Sign In</h2>

          <form onSubmit={handleSubmit} className="space-y-5" autoComplete="off">
            <div>
              <Label htmlFor="email" className="text-xs text-[#888]">
                Email
              </Label>
              <div className="relative mt-1">
                <Mail
                  className="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-[#888]"
                  size={16}
                />
                <Input
                  id="email"
                  type="email"
                  data-testid="admin-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="off"
                  inputMode="email"
                  className="luxury-input admin-login-input admin-login-input--with-left-icon"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-xs text-[#888]">
                Password
              </Label>
              <div className="relative mt-1">
                <Lock
                  className="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-[#888]"
                  size={16}
                />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  data-testid="admin-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="off"
                  className="luxury-input admin-login-input admin-login-input--with-both-icons"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-4 top-1/2 z-10 -translate-y-1/2 text-[#888] hover:text-[#111]"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              data-testid="admin-login-btn"
              disabled={isLoading}
              className="btn-black w-full disabled:opacity-50"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="spinner" /> Signing In...
                </span>
              ) : (
                "Sign In"
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-[#888] mt-6">
          Protected area. Authorized personnel only.
        </p>
      </div>
    </div>
  );
}
